import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ShieldCheck, Users, ArrowRight, Pill, Droplets, Heart, Scale, Sparkles, Check } from "lucide-react";
import api from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { WelcomeVideo } from "../components/WelcomeVideo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ICONS = { Pill, Droplets, Heart, Scale, Sparkles };
const HERO_CAT = "https://images.unsplash.com/photo-1768859336207-3c3ba2027cca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxwbGF5ZnVsJTIwY2F0JTIwd2hpdGUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MjcwNjA0OXww&ixlib=rb-4.1.0&q=85";
const SENIOR_CAT = "https://images.unsplash.com/photo-1587417639866-0da8bb24b7d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxzZW5pb3IlMjBjYXQlMjBzbGVlcGluZ3xlbnwwfHx8fDE3ODI3MDYwNDh8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [collections, setCollections] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/collections").then((r) => setCollections(r.data));
    api.get("/products?featured=true").then((r) => setFeatured(r.data.slice(0, 4)));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/faqs").then((r) => setFaqs(r.data));
  }, []);

  return (
    <div className="bg-white">
      <WelcomeVideo />
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 lg:pt-16 lg:pb-28">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-1.5 text-xs font-bold border border-gray-200 mb-6">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Veterinary Reviewed Curation
              </span>
              <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
                Care for the<br />cat who needs<br />you most.
              </h1>
              <p className="text-lg text-gray-600 max-w-md mb-8 leading-relaxed">
                Trusted products and practical guidance for owners managing difficult, recurring problems — built for senior cats and cats with chronic conditions.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/shop" data-testid="hero-shop-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FF5C28] text-white font-bold px-8 py-4 hover:bg-[#E04D1E] hover:-translate-y-0.5 transition-all shadow-lg shadow-[#FF5C28]/25">
                  Shop Collections <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                </Link>
                <Link to="/shop?collection=bundles" data-testid="hero-bundles-btn"
                  className="inline-flex items-center rounded-full bg-white text-[#0F0F0F] font-bold px-8 py-4 border-2 border-gray-200 hover:border-[#FF5C28] transition-colors">
                  View Starter Kits
                </Link>
              </div>
              <div className="flex items-center gap-8 mt-10">
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#FF5C28] text-[#FF5C28]" />)}
                  </div>
                  <p className="text-sm font-semibold mt-1">5-Star rated care</p>
                </div>
                <div className="border-l border-gray-300 pl-8">
                  <p className="font-display font-black text-3xl">13K+</p>
                  <p className="text-sm text-gray-500">Trusted by cat owners</p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
              className="relative">
              <div className="cat-blob aspect-square max-w-md mx-auto relative">
                <img src={HERO_CAT} alt="Happy cat" className="w-full h-full object-cover rounded-[40%_60%_55%_45%/55%_45%_55%_45%]" />
              </div>
              <motion.div animate={{ y: [-6, 6, -6] }} transition={{ duration: 4, repeat: Infinity }}
                className="absolute top-6 -left-2 md:left-4 bg-white rounded-2xl px-4 py-3 shadow-xl flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#FF5C28] flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Every product</p>
                  <p className="text-sm font-bold leading-tight">Vet Reviewed</p>
                </div>
              </motion.div>
              <motion.div animate={{ y: [6, -6, 6] }} transition={{ duration: 4.5, repeat: Infinity }}
                className="absolute bottom-8 -right-2 md:right-4 bg-white rounded-2xl px-4 py-3 shadow-xl flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#0F0F0F] flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Loved by</p>
                  <p className="text-sm font-bold leading-tight">13,000+ owners</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { icon: ShieldCheck, t: "Vet Reviewed" },
            { icon: Heart, t: "Senior-Cat Focused" },
            { icon: Check, t: "Free Shipping $50+" },
            { icon: Sparkles, t: "Curated, Not Cluttered" },
          ].map((x) => (
            <div key={x.t} className="flex items-center justify-center gap-2 text-sm font-bold text-gray-700">
              <x.icon className="w-5 h-5 text-[#FF5C28]" strokeWidth={2.5} /> {x.t}
            </div>
          ))}
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-[#FF5C28] font-bold text-sm uppercase tracking-wide">Shop by problem</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-2">Five problems, solved.</h2>
          </div>
          <p className="text-gray-500 max-w-sm">Every collection is built around a real, recurring challenge senior-cat owners face daily.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collections.map((c, idx) => {
            const Icon = ICONS[c.icon] || Heart;
            return (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: idx * 0.05 }}>
                <Link to={`/shop?collection=${c.slug}`} data-testid={`collection-${c.slug}`}
                  className={`group block rounded-3xl overflow-hidden border border-gray-100 hover:border-[#FF5C28] transition-all h-full ${idx === 0 ? "lg:row-span-2" : ""}`}>
                  <div className="relative bg-[#F8F9FA] aspect-[4/3] overflow-hidden">
                    <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-lg">
                      <Icon className="w-6 h-6 text-[#FF5C28]" strokeWidth={2.5} />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-bold text-xl mb-1 group-hover:text-[#FF5C28] transition-colors">{c.name}</h3>
                    <p className="text-sm text-gray-500 mb-3">{c.description}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-[#FF5C28]">Shop now <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-[#F8F9FA] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">Owner favorites</h2>
            <Link to="/shop" className="hidden md:inline-flex items-center gap-1 font-bold text-[#FF5C28]">View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </div>
      </section>

      {/* CARE PROMISE / SENIOR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={SENIOR_CAT} alt="Senior cat resting" className="rounded-3xl w-full object-cover aspect-[4/3]" />
            <div className="absolute -bottom-6 -right-2 md:right-6 bg-[#FF5C28] text-white rounded-2xl px-6 py-4 shadow-xl">
              <p className="font-display font-black text-3xl">10+ yrs</p>
              <p className="text-sm opacity-90">of senior-cat expertise</p>
            </div>
          </div>
          <div>
            <span className="text-[#FF5C28] font-bold text-sm uppercase tracking-wide">Our promise</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-2 mb-6">We organize care.<br />We never replace your vet.</h2>
            <div className="space-y-4">
              {[
                "Curated, not overwhelming — only products that actually solve problems.",
                "Vet-reviewed selections you can trust for chronic & senior care.",
                "Bundles, subscriptions and guides that take the guesswork away.",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />
                  </div>
                  <p className="text-gray-700 font-medium">{t}</p>
                </div>
              ))}
            </div>
            <Link to="/about" className="inline-flex items-center gap-2 rounded-full bg-[#0F0F0F] text-white font-bold px-8 py-4 mt-8 hover:bg-[#FF5C28] transition-colors">
              Learn our story <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#0F0F0F] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight text-center mb-12">Loved by cat owners</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-3xl p-6">
                <div className="flex gap-1 mb-3">{[...Array(t.rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#FF5C28] text-[#FF5C28]" />)}</div>
                <p className="text-gray-300 mb-5 leading-relaxed">"{t.text}"</p>
                <p className="font-bold">{t.name}</p>
                <p className="text-sm text-gray-500">with {t.cat}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND VIDEO */}
      <section className="bg-[#0F0F0F] text-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#FF5C28] font-bold text-sm uppercase tracking-wide">Our story in motion</span>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-2 mb-3">Meet Meow Care Love</h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-10">Only the best for our loves — see what we're all about.</p>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-black">
            <video
              src="https://customer-assets.emergentagent.com/job_store-flow-78/artifacts/cwfy1deh_Make_a_Cat_Promotional_video_f%20%281%29.mp4"
              controls
              playsInline
              preload="metadata"
              className="w-full max-h-[70vh] object-contain bg-black"
              data-testid="home-brand-video"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight text-center mb-10">Questions, answered</h2>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="bg-[#F8F9FA] rounded-2xl px-6 border-none">
              <AccordionTrigger className="font-display font-bold text-left hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
